import { Avatar, Tooltip, Typography } from "@mui/material";
import PropTypes from "prop-types";
import { stringToColor } from "../utils/avatarColor";
import { resolveImageUrl } from "../utils/imageUrl";

/**
 * UserAvatar component - displays user avatar image or initials
 * @param {string} name - The user/account name to display initials from
 * @param {string} image - The avatar image URL (optional)
 * @param {number} size - The size of the avatar (width and height in pixels)
 * @param {string} fontSize - The font size of the initials (optional, defaults based on size)
 * @param {string} tooltip - Tooltip text to show on hover (optional)
 * @param {object} sx - Additional MUI sx prop styles (optional)
 */
export const UserAvatar = ({
  name,
  image,
  size = 56,
  fontSize,
  tooltip,
  sx = {},
}) => {
  // Calculate font size based on avatar size if not provided
  // Use approximately 40% of the avatar size for better proportions
  const defaultFontSize = fontSize || `${size * 0.4}px`;

  // Get initials from name (first letter of first two words, or just first letter)
  const getInitials = (name) => {
    if (!name) return "?";
    const words = name.trim().split(" ");
    if (words.length >= 2) {
      return (words[0].charAt(0) + words[1].charAt(0)).toUpperCase();
    }
    return name.charAt(0).toUpperCase();
  };

  const avatar = (
    <Avatar
      src={image ? resolveImageUrl(image) : undefined}
      alt={name || "User avatar"}
      sx={{
        width: size,
        height: size,
        bgcolor: stringToColor(name),
        fontSize: fontSize || defaultFontSize,
        fontWeight: 600,
        flexShrink: 0,
        ...sx,
      }}
    >
      {/* Fallback to initials if image fails to load or is not provided */}
      <Typography
        component="span"
        sx={{
          fontSize: fontSize || defaultFontSize,
          fontWeight: 600,
          color: "#fff",
        }}
      >
        {getInitials(name)}
      </Typography>
    </Avatar>
  );

  // If tooltip is provided, wrap the avatar with Tooltip
  if (tooltip) {
    return (
      <Tooltip title={tooltip} arrow>
        {avatar}
      </Tooltip>
    );
  }

  return avatar;
};

UserAvatar.propTypes = {
  name: PropTypes.string,
  image: PropTypes.string,
  size: PropTypes.number,
  fontSize: PropTypes.string,
  tooltip: PropTypes.string,
  sx: PropTypes.object,
};
